import type { ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type KanbanAccent = 'gold' | 'navy' | 'green' | 'red'

export interface KanbanColumnProps {
  title: string
  /** Podpis po prawej w nagłówku, np. „5 dokumentów". */
  meta?: ReactNode
  accent?: KanbanAccent
  children: ReactNode
  className?: string
}

const ACCENTS: Record<KanbanAccent, string> = {
  gold: 'bg-gold',
  navy: 'bg-navy-800',
  green: 'bg-success',
  red: 'bg-danger',
}

/** Kolumna tablicy dekretacji — nagłówek z pionowym akcentem + lista kart. */
export function KanbanColumn({ title, meta, accent = 'navy', children, className }: KanbanColumnProps) {
  return (
    <section className={cn('am-card flex min-w-0 flex-col', className)}>
      <header className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={cn('h-[14px] w-[3px] shrink-0 rounded-sm', ACCENTS[accent])} />
          <h3 className="truncate text-base font-semibold text-ink">{title}</h3>
        </div>
        {meta ? <span className="shrink-0 text-micro text-muted">{meta}</span> : null}
      </header>
      <div className="flex flex-col gap-3 p-3.5">{children}</div>
    </section>
  )
}

export interface KanbanCardProps {
  /** Tag kategorii, np. „Postanowienie". */
  tag?: ReactNode
  /** Sygnatura po prawej (monospace). */
  signature?: string | null
  title: ReactNode
  /** Lewa strona stopki — zwykle Avatar + nazwisko. */
  footerLeft?: ReactNode
  /** Prawa strona stopki — czas oczekiwania. */
  footerRight?: ReactNode
  /** Podświetla stopkę na czerwono (zaległość). */
  urgent?: boolean
  onClick?: () => void
  className?: string
}

/** Karta dokumentu na tablicy dekretacji. */
export function KanbanCard({
  tag,
  signature,
  title,
  footerLeft,
  footerRight,
  urgent = false,
  onClick,
  className,
}: KanbanCardProps) {
  return (
    <article
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      className={cn(
        'am-card overflow-hidden',
        onClick && 'cursor-pointer transition-colors duration-fast hover:border-line focus-visible:am-focus focus:outline-none',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3 px-3.5 pt-3">
        {tag ?? <span />}
        {signature ? <span className="t-mono shrink-0 text-muted">{signature}</span> : null}
      </div>
      <p className="px-3.5 pb-3 pt-2.5 text-base font-semibold leading-snug text-ink">{title}</p>
      {footerLeft || footerRight ? (
        <div className="flex items-center justify-between gap-3 border-t border-line-soft px-3.5 py-2.5">
          <div className="flex min-w-0 items-center gap-2 text-small text-ink">{footerLeft}</div>
          <span className={cn('shrink-0 text-micro', urgent ? 'font-medium text-danger' : 'text-muted')}>
            {footerRight}
          </span>
        </div>
      ) : null}
    </article>
  )
}

/** Siatka kolumn tablicy (desktop 3 kolumny, poniżej — jedna). */
export function KanbanBoard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid grid-cols-1 items-start gap-5 lg:grid-cols-3', className)}>{children}</div>
  )
}
